import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Truck, CarFront, Battery, MountainSnow, Users, DollarSign, Tag,
  Search, MapPin, MessageSquareText, Quote, Calendar, BellRing,
  HelpCircle, ChevronRight, CheckCircle2, ShieldCheck, Sparkles, Handshake,
  ArrowRight, Wand2, ClipboardCheck, ListChecks, Send,
} from "lucide-react";
import Seo, { orgSchema, websiteSchema, breadcrumbs, personSchema } from "../components/Seo";
import CustomerStories from "../components/CustomerStories";
import CtaCallout from "../components/CtaCallout";
import LiveInventory from "../components/LiveInventory";
import HeroContactCard from "../components/HeroContactCard";
// ReviewCta is now gated behind lead-submit success only.
import { siteConfig } from "../config";
import { MAKES } from "../data/catalog";

import { VERSION } from "../version";

const NEEDS = [
  { icon: Truck, title: "A Truck", to: "/new-vehicles/trucks" },
  { icon: CarFront, title: "An SUV", to: "/new-vehicles/suvs" },
  { icon: Battery, title: "An EV", to: "/new-vehicles/evs" },
  { icon: MountainSnow, title: "AWD / 4x4", to: "/shop-by-need/awd-4x4" },
  { icon: DollarSign, title: "Something Under $25k", to: "/used-vehicles/under-25000" },
  { icon: Users, title: "A Family Vehicle", to: "/shop-by-need/family-vehicles" },
  { icon: Tag, title: "Low Payment Options", to: "/shop-by-need/payment" },
];

const TOOLS = [
  { icon: Tag, title: "Value My Trade", desc: "See what your trade could be worth.", cta: "Get Your Value", to: "/value-my-trade" },
  { icon: DollarSign, title: "Payment Help", desc: "Get a real-world idea of what fits your budget.", cta: "See Payment Options", to: "/payment-help" },
  { icon: Calendar, title: "Schedule a Test Drive", desc: "Pick a time that works best for you.", cta: "Schedule Now", to: "/schedule-test-drive" },
  { icon: BellRing, title: "Vehicle Alerts", desc: "I'll let you know when the right one shows up.", cta: "Set Up Alerts", to: "/vehicle-alerts" },
  { icon: HelpCircle, title: "Ask a Question", desc: "Ask about vehicles, payments, trades, EVs, or next steps.", cta: "Message Scott", to: "/contact" },
];

const HOW_IT_WORKS = [
  {
    icon: ClipboardCheck,
    title: "Tell Me What You Need",
    text: "Share the body style, budget, payment goal, trade-in, features, and timeline that matter most.",
  },
  {
    icon: ListChecks,
    title: "I Narrow Down Real Options",
    text: "I'll help compare new Chevy options and used vehicles from many brands so you do not have to guess.",
  },
  {
    icon: Send,
    title: "You Get Clear Next Steps",
    text: "Text, call, schedule a test drive, value your trade, or get payment direction without pressure.",
  },
];

const HOME_FAQS = [
  {
    question: "Can Scott help me find a used vehicle from another brand?",
    answer:
      "Yes. New vehicle shopping is focused on Chevrolet, but used vehicle help can include many brands, including Ford, Toyota, Honda, Jeep, Ram, GMC, Cadillac, Mercedes-Benz, Porsche, Jaguar, BMW, Audi, Lexus, Acura, Tesla, Rivian, and more.",
  },
  {
    question: "Can Scott help me with trade-in value?",
    answer:
      "Yes. Scott can help you get started with trade-in information, vehicle details, payoff questions, and next steps for getting a real value direction.",
  },
  {
    question: "Can Scott help me understand payments?",
    answer:
      "Yes. Scott can help you think through payment goals, down payment, trade-in equity, term length, lease vs finance basics, and what vehicle options may fit your budget.",
  },
  {
    question: "Does Scott help with EV questions?",
    answer:
      "Yes. Scott can help with Chevrolet EV questions, including Equinox EV, Blazer EV, Silverado EV, range, charging basics, home charging, and lease or finance questions.",
  },
  {
    question: "What areas does Scott serve?",
    answer:
      "Scott helps drivers across Central Ohio, including Grove City, Columbus, Hilliard, Dublin, Gahanna, Westerville, Reynoldsburg, Pickerington, Groveport, Canal Winchester, Ashville, Circleville, London, Plain City, Delaware, Marysville, Pataskala, Lancaster, Chillicothe, Newark, Whitehall, and nearby communities.",
  },
  {
    question: "Can I text Scott directly?",
    answer:
      "Yes. You can text Scott directly for vehicle questions, trade-in help, payment direction, EV questions, or help finding the right new Chevy or used vehicle.",
  },
];

const homepageFaqSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQS.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

const homepageWebPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Scott Sells Chevys",
  description:
    "Personal help shopping for a new Chevy or used vehicle in Central Ohio with trade-in help, payment guidance, EV questions, and no-pressure vehicle support.",
  url: "https://scottsellschevys.com/",
  mainEntityOfPage: "https://scottsellschevys.com/",
  about: [
    { "@type": "Thing", name: "New Chevrolet vehicles" },
    { "@type": "Thing", name: "Used vehicles" },
    { "@type": "Thing", name: "Trade-in help" },
    { "@type": "Thing", name: "Payment guidance" },
    { "@type": "Thing", name: "Chevrolet EV education" },
  ],
});

export default function Home() {
  const navigate = useNavigate();
  const [s, setS] = useState({ status: "", body_style: "", make: "", max_price: "" });

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (s.status) params.set("status", s.status);
    if (s.body_style) params.set("body_style", s.body_style);
    if (s.make) params.set("make", s.make);
    if (s.max_price) params.set("max_price", s.max_price);
    navigate(`/inventory?${params.toString()}`);
  };

  return (
    <>
      <Seo
        title="Scott Sells Chevys | New Chevy & Used Vehicle Help in Central Ohio"
        description="Get personal help shopping for a new Chevy or used vehicle in Central Ohio. Text Scott for trade-in help, payment guidance, EV questions, and no-pressure vehicle support."
        canonicalPath="/"
        jsonLd={[
          orgSchema(),
          websiteSchema(),
          personSchema(),
          homepageWebPageSchema(),
          homepageFaqSchema(),
          breadcrumbs([{ name: "Home", path: "/" }]),
        ]}
      />

      <section className="ssc-home-hero" data-testid="home-hero">
        <div className="ssc-home-hero-bg" aria-hidden="true" />
        <div className="ssc-container relative pt-6 sm:pt-10 md:pt-14 pb-0 grid grid-cols-12 gap-4 sm:gap-6 md:gap-10 items-end">
          <div className="col-span-4 md:col-span-3 lg:col-span-3 ssc-hero-scott-wrap">
            <img
              src={siteConfig.scott.photo}
              alt={siteConfig.scott.photoAlt}
              className="ssc-hero-scott ssc-fade-in"
              loading="eager"
              data-testid="hero-scott-photo"
            />
          </div>

          <div className="col-span-8 md:col-span-9 lg:col-span-9 ssc-fade-in pb-8 sm:pb-10 md:pb-16">
            <div className="ssc-hero-ticker" aria-hidden="true" data-testid="hero-badges">
              <span>
                <span className="dot" />
                <span className="ssc-badge-text">
                  <span className="ssc-badge-top">Available today</span>
                  <span className="ssc-badge-bot">Text {siteConfig.scott.phone}</span>
                </span>
              </span>
              <span className="hidden sm:inline-flex">
                <Tag size={14} className="text-[var(--ssc-blue-2)]" />
                <span className="ssc-badge-text">
                  <span className="ssc-badge-top">New Chevy</span>
                  <span className="ssc-badge-bot">Used Many Brands</span>
                </span>
              </span>
              <span className="hidden md:inline-flex">
                <ShieldCheck size={14} className="text-[var(--ssc-gold)]" />
                <span className="ssc-badge-text">
                  <span className="ssc-badge-top">No-pressure</span>
                  <span className="ssc-badge-bot">Shopping</span>
                </span>
              </span>
            </div>

            <h1 className="ssc-display text-[26px] sm:text-[36px] md:text-5xl xl:text-6xl font-black leading-[1.02] text-white tracking-tight mt-2 sm:mt-4" data-testid="hero-h1">
              <span className="block">I&rsquo;ll Help You Find</span>
              <span className="block">The <em className="ssc-right-word">Right</em> Chevy.</span>
            </h1>

            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-white/85 max-w-2xl" data-testid="hero-sub">
              New Chevy or used from many brands, I&rsquo;ll help you compare real options, answer your questions, and make the process easier with <span className="ssc-hero-gold">over 15 years</span> of vehicle-shopping experience.
            </p>

            <form onSubmit={onSearch} className="ssc-search-bar mt-7" data-testid="home-search-form">
              <select className="ssc-search-field" value={s.status} onChange={(e) => setS({ ...s, status: e.target.value })} data-testid="search-status">
                <option value="">New or Used</option>
                <option value="new">New</option>
                <option value="used">Used</option>
              </select>

              <span className="ssc-search-divider" />

              <select className="ssc-search-field" value={s.body_style} onChange={(e) => setS({ ...s, body_style: e.target.value })} data-testid="search-type">
                <option value="">Any Type</option>
                <option value="truck">Truck</option>
                <option value="suv">SUV</option>
                <option value="car">Car</option>
                <option value="ev">EV</option>
              </select>

              <span className="ssc-search-divider" />

              <select className="ssc-search-field" value={s.make} onChange={(e) => setS({ ...s, make: e.target.value })} data-testid="search-make">
                <option value="">Any Make</option>
                {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>

              <span className="ssc-search-divider" />

              <select className="ssc-search-field" value={s.max_price} onChange={(e) => setS({ ...s, max_price: e.target.value })} data-testid="search-price">
                <option value="">Max Price</option>
                <option value="20000">Under $20,000</option>
                <option value="25000">Under $25,000</option>
                <option value="30000">Under $30,000</option>
                <option value="40000">Under $40,000</option>
                <option value="50000">Under $50,000</option>
                <option value="75000">Under $75,000</option>
              </select>

              <button type="submit" className="ssc-search-btn" data-testid="search-submit">
                <Search size={18} /> <span>Search Inventory</span>
              </button>
            </form>
          </div>

          {/* Full-width quick-action card row — sits below the search bar and
              spans the full hero width so the first card overlaps Scott's
              lower-left, hiding the natural photo crop on desktop. On mobile
              the row stacks cleanly below Scott. v6.4.3. */}
          <div className="col-span-12 ssc-fade-in pb-8 sm:pb-10 md:pb-14">
            <div className="ssc-quick-row" data-testid="home-quick-actions">
              {[
                { icon: Wand2, l1: "Have Scott", l2: "Find One For Me", to: "/find-me-a-vehicle" },
                { icon: Tag, l1: "Value My Trade", l2: "Get Your Instant Trade-In Value", to: "/value-my-trade" },
                { icon: DollarSign, l1: "See Payment", l2: "View Real Payment Options", to: "/payment-help" },
                { icon: MessageSquareText, l1: "Text or Call Scott", l2: "Anytime — I'm Here to Help!", to: siteConfig.scott.phoneSms, ext: true },
              ].map((q, i) => {
                const I = q.icon;
                const Inner = (
                  <>
                    <div className="ssc-quick-icon" aria-hidden="true">
                      <I size={20} strokeWidth={2} />
                    </div>
                    <div className="leading-tight flex-1 min-w-0">
                      <div className="font-extrabold text-[var(--ssc-ink)] text-[15px] truncate">{q.l1}</div>
                      <div className="text-[var(--ssc-ink-2)] text-xs line-clamp-2">{q.l2}</div>
                    </div>
                    <ChevronRight size={18} strokeWidth={2.5} className="ssc-quick-arrow" />
                  </>
                );

                return q.ext ? (
                  <a key={q.to} href={q.to} className="ssc-quick" data-testid={`quick-${i}`}>{Inner}</a>
                ) : (
                  <Link key={q.to} to={q.to} className="ssc-quick" data-testid={`quick-${i}`}>{Inner}</Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* HeroContactCard removed in v6.4.1 per mockup — the CTAs are
          already covered by the in-hero quick-action cards. */}

      <section className="bg-[var(--ssc-navy-2)] text-white border-t border-white/5" data-testid="trust-strip">
        <div className="ssc-container py-5 sm:py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:divide-x md:divide-white/10">
          {[
            { icon: Handshake, top: "Personalized Service", bot: "From start to finish" },
            { icon: ShieldCheck, top: "15+ Years Experience", bot: "Helping local car buyers" },
            { icon: CheckCircle2, top: "No Pressure, Ever", bot: "Honest help you can trust" },
            { icon: MapPin, top: "Proudly Serving", bot: "Grove City, OH & beyond" },
          ].map((t, i) => {
            const I = t.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 md:px-5"
                data-testid={`trust-strip-item-${i}`}
              >
                <I
                  size={28}
                  className="text-[var(--ssc-gold)] shrink-0"
                  strokeWidth={1.6}
                />
                <div className="leading-tight">
                  <div className="font-extrabold text-sm md:text-base">{t.top}</div>
                  <div className="text-white/65 text-xs md:text-sm">{t.bot}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="ssc-section bg-white" data-testid="how-scott-helps">
        <div className="ssc-container">
          <div className="max-w-3xl">
            <h2 className="ssc-display text-3xl md:text-4xl font-black text-[var(--ssc-ink)]">
              A Simpler Way to Shop for a Vehicle
            </h2>
            <p className="mt-3 text-[var(--ssc-ink-2)] text-sm md:text-base">
              Dealer sites can feel like a maze. This site is built to make the next step clearer, whether you are shopping for a new Chevy, comparing used vehicles from many brands, checking your trade, or trying to stay near a payment goal.
            </p>
          </div>

          <div className="mt-6 sm:mt-7 grid sm:grid-cols-3 gap-3 sm:gap-4">
            {HOW_IT_WORKS.map((item, i) => {
              const I = item.icon;
              return (
                <div key={item.title} className="ssc-shop-help-card" data-testid={`how-step-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-[var(--ssc-paper)] border border-[var(--ssc-line)] grid place-items-center">
                    <I size={25} className="text-[var(--ssc-blue)]" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-4 text-lg font-black text-[var(--ssc-ink)]">{item.title}</h3>
                  <p className="mt-2 text-sm text-[var(--ssc-ink-2)]">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ssc-section bg-[var(--ssc-paper)]">
        <div className="ssc-container grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <h2 className="ssc-display text-3xl md:text-4xl font-black text-[var(--ssc-ink)]">I'm looking for…</h2>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="looking-for-cards">
              {NEEDS.map((n) => {
                const I = n.icon;
                return (
                  <Link key={n.title} to={n.to} className="ssc-need-card group" data-testid={`need-${n.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <I size={32} strokeWidth={1.6} className="text-[var(--ssc-ink)] group-hover:text-[var(--ssc-blue)] transition-colors" />
                    <div className="font-bold text-[var(--ssc-ink)] text-center mt-3">{n.title}</div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <p className="text-[var(--ssc-ink-2)]">Not sure yet? That's okay. I can help you narrow it down.</p>
              <svg width="40" height="24" viewBox="0 0 40 24" className="text-[var(--ssc-blue)] hidden sm:block" aria-hidden="true">
                <path d="M2 12 Q 18 -2 32 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
                <path d="M28 8 L 34 12 L 28 16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-primary" data-testid="have-scott-find">
                Have Scott Find One For Me
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="ssc-shop-help-card" data-testid="shop-help-card">
              <h3 className="text-xl font-black text-[var(--ssc-blue)]">Let Me Do the Shopping</h3>
              <p className="text-[var(--ssc-ink-2)] mt-2 text-sm">
                Tell me what you need, your budget, and what matters most. I'll send you the best options. <Link to="/about-scott" className="font-bold text-[var(--ssc-blue)] hover:underline">With over 15 years in the car business</Link>, I can help you narrow down what actually makes sense.
              </p>

              <ul className="mt-4 space-y-2 text-sm">
                {[
                  "New Chevy or used from many brands",
                  "Payments that fit your budget",
                  "Trade-in help",
                  "Options that match your needs",
                ].map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <CheckCircle2 size={18} className="text-[var(--ssc-blue)] shrink-0 mt-0.5" />
                    <span className="text-[var(--ssc-ink)]">{it}</span>
                  </li>
                ))}
              </ul>

              <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-primary w-full mt-5" data-testid="shop-help-get-started">
                Get Started <ArrowRight size={16} />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="ssc-section bg-white" data-testid="new-used-section">
        <div className="ssc-container grid sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="ssc-vbig ssc-vbig-new" data-testid="card-new-vehicles">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="ssc-vbig-label">New</div>
                <h3 className="ssc-display text-3xl md:text-4xl font-black text-[var(--ssc-blue)] mt-1">New Chevy Vehicles</h3>
                <p className="text-[var(--ssc-ink-2)] mt-2">Explore the latest Chevrolet cars, trucks, SUVs, and EVs.</p>
              </div>

              <img
                src="https://res.cloudinary.com/diqi6ihgx/image/upload/v1780029886/719d7f9f-ca80-45bc-bebe-08e052076382_wafdku.jpg"
                alt="New Chevrolet vehicle on Scott's lot"
                className="hidden sm:block w-32 md:w-44 lg:w-56 rounded-xl object-cover aspect-[4/3] shrink-0"
                loading="lazy"
              />
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/new-vehicles" className="ssc-btn ssc-btn-primary" data-testid="card-new-search">Search New Inventory</Link>
              <Link to="/new-vehicles/specials" className="ssc-btn ssc-btn-outline-blue" data-testid="card-new-specials">New Vehicle Specials</Link>
              <Link to="/new-vehicles/lease-options" className="ssc-btn ssc-btn-outline-blue" data-testid="card-new-lease">Lease Options</Link>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 text-xs text-[var(--ssc-ink-2)]">
              {["Full factory warranty", "Latest features", "Built your way"].map((f) => (
                <div key={f} className="flex items-center gap-1.5">
                  <Sparkles size={14} className="text-[var(--ssc-blue)] shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>

          <div className="ssc-vbig ssc-vbig-used" data-testid="card-used-vehicles">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="ssc-vbig-label">Used</div>
                <h3 className="ssc-display text-3xl md:text-4xl font-black text-[var(--ssc-red)] mt-1">Used Vehicles</h3>
                <p className="text-[var(--ssc-ink-2)] mt-2">Quality used cars, trucks, SUVs, and EVs from many brands.</p>
              </div>

              <img
                src="https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030069/camaro_ijjepv.webp"
                alt="Used Chevrolet Camaro"
                className="hidden sm:block w-32 md:w-44 lg:w-56 rounded-xl object-cover aspect-[4/3] shrink-0"
                loading="lazy"
              />
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/used-vehicles" className="ssc-btn ssc-btn-red" data-testid="card-used-search">Search Used Inventory</Link>
              <Link to="/used-vehicles/one-owner" className="ssc-btn ssc-btn-outline-red" data-testid="card-used-one-owner">One-Owner Used</Link>
              <Link to="/used-vehicles/fresh-trades" className="ssc-btn ssc-btn-outline-red" data-testid="card-used-fresh-trades">Fresh Trades</Link>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 text-xs text-[var(--ssc-ink-2)]">
              {["Many brands available", "Great value", "Fresh trades"].map((f) => (
                <div key={f} className="flex items-center gap-1.5">
                  <Sparkles size={14} className="text-[var(--ssc-red)] shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ssc-section bg-[var(--ssc-paper)]" data-testid="home-live-inventory">
        <div className="ssc-container">
          <LiveInventory
            title="Live Inventory — Popular Picks"
            subtitle="Real vehicles available right now, updated daily from the live inventory feed."
            params={{}}
            limit={8}
            viewAllTo="/inventory"
            testId="home-featured-inventory"
          />
        </div>
      </section>

      <CustomerStories />

      <section className="ssc-container" data-testid="home-cta-section">
        <CtaCallout
          testId="home-cta-callout"
          variant="navy"
          heading="Want Scott to do the searching?"
          sub="Tell Scott what you're after: new Chevy, used from any brand, body style, budget, trade, and timeline. He'll send real options that fit."
          actions={[
            { key: "find", label: "Find Me a Vehicle", icon: "find", to: "/find-me-a-vehicle" },
            { key: "payment", label: "Payment Help", icon: "payment", to: "/payment-help" },
            { key: "trade", label: "Value My Trade", icon: "trade", to: "/value-my-trade" },
            { key: "text", label: "Text Scott", icon: "text", href: siteConfig.scott.phoneSms },
          ]}
        />
      </section>

      <section className="ssc-tools-strip" data-testid="tools-strip">
        <div className="ssc-container">
          <h2 className="ssc-display text-2xl md:text-3xl font-black text-[var(--ssc-ink)] mb-6 md:mb-8">Helpful Tools to Make Things Easier</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 lg:divide-x divide-[var(--ssc-line)]">
            {TOOLS.map((t, i) => {
              const I = t.icon;
              return (
                <Link key={t.to} to={t.to} className="ssc-tool" data-testid={`tool-${i}`}>
                  <I size={28} strokeWidth={1.6} className="text-[var(--ssc-blue)]" />
                  <div className="mt-3">
                    <div className="font-extrabold text-[var(--ssc-ink)]">{t.title}</div>
                    <p className="text-sm text-[var(--ssc-ink-2)] mt-1">{t.desc}</p>
                    <div className="text-[var(--ssc-blue)] font-bold text-sm mt-2 inline-flex items-center gap-1">
                      {t.cta} <ChevronRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ssc-section bg-[var(--ssc-paper)]" data-testid="home-faq-section">
        <div className="ssc-container">
          <div className="max-w-3xl">
            <h2 className="ssc-display text-3xl md:text-4xl font-black text-[var(--ssc-ink)]">
              Common Questions
            </h2>
            <p className="mt-3 text-[var(--ssc-ink-2)] text-sm md:text-base">
              Quick answers about shopping new Chevys, used vehicles from many brands, trades, payments, EVs, and working directly with Scott.
            </p>
          </div>

          <div className="mt-6 sm:mt-7 grid sm:grid-cols-2 gap-3 sm:gap-4">
            {HOME_FAQS.map((faq, i) => (
              <div key={faq.question} className="ssc-shop-help-card" data-testid={`home-faq-${i}`}>
                <h3 className="font-black text-[var(--ssc-ink)]">{faq.question}</h3>
                <p className="mt-2 text-sm text-[var(--ssc-ink-2)]">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/faq" className="ssc-btn ssc-btn-outline-blue" data-testid="home-faq-more">
              Visit Full FAQ
            </Link>
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary" data-testid="home-faq-text">
              <MessageSquareText size={16} /> Text Scott
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ssc-navy)] text-white relative overflow-hidden" data-testid="review-band">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #ffffff 1px, transparent 1px)", backgroundSize: "30px 30px" }}
        />

        <div className="ssc-container py-10 sm:py-14 md:py-20 grid lg:grid-cols-12 gap-6 sm:gap-8 items-center relative">
          <div className="lg:col-span-5">
            <Quote size={48} className="text-[var(--ssc-blue)]" strokeWidth={1.5} />
            <blockquote className="text-2xl md:text-[28px] font-bold leading-snug mt-3">
              "Scott was very knowledgeable and professional. He was friendly and very patient with us."
            </blockquote>
            <div className="mt-3 text-[var(--ssc-silver)] text-xs font-semibold uppercase tracking-widest">— Customer Review</div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xl md:text-2xl font-black ssc-display leading-tight">
              Helpful. Knowledgeable.<br /><span className="text-[var(--ssc-blue)]">No pressure.</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/reviews" className="ssc-btn ssc-btn-primary" data-testid="review-read-more">Read More Reviews</Link>
              <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-ghost" data-testid="review-text-scott">
                <MessageSquareText size={16} /> Text Scott
              </a>
            </div>
            <div className="mt-5">
              {/* ReviewCta intentionally removed — shown only on lead-submit success now. */}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
              <img
                src="https://res.cloudinary.com/diqi6ihgx/image/upload/v1780030163/me_and_customer_lg68xt.jpg"
                alt="Scott Hall delivering a vehicle to a happy customer"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
                data-testid="review-band-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ssc-navy)]/40 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[var(--ssc-navy)] text-[var(--ssc-silver)] border-t border-white/10" data-testid="build-version-footer">
        <div className="ssc-container py-3 text-center text-[11px] tracking-wide">
          Scott Sells Chevys · Version {VERSION}
        </div>
      </footer>
    </>
  );
}